import { ApiErrorCode } from '@fleet/shared';
import { ApiError } from '../api/client';

/**
 * Fehleranzeige, die auf dem Fehlercode verzweigt statt auf dem Text.
 *
 * Verallgemeinert aus dem Buchungsdialog. Der Nutzen liegt in der Zusatzzeile:
 * Eine fehlende Rolle ist kein Bedienfehler und braucht einen anderen Hinweis
 * als ein belegter Zeitslot oder ein nicht erreichbares Verzeichnis. An der
 * Formulierung ließe sich das nicht zuverlässig unterscheiden.
 */
const ADVICE: Partial<Record<string, string>> = {
  [ApiErrorCode.MISSING_ROLE]:
    'Für diesen Bereich braucht es die Rolle Administrator oder Fuhrparkleitung.',
  [ApiErrorCode.BOOKING_SLOT_TAKEN]:
    'Jemand war schneller. Nach dem Neuladen zeigt der Kalender den aktuellen Stand.',
  [ApiErrorCode.DIRECTORY_UNAVAILABLE]:
    'Das ist eine Störung der Verzeichnisabfrage — nicht dasselbe wie „keine Treffer“.',
  [ApiErrorCode.GROUP_RESOLUTION_UNAVAILABLE]:
    'Die Entra-Gruppen sind gerade nicht auflösbar. Berechtigungen werden deshalb bewusst verweigert statt geraten.',
  [ApiErrorCode.PRIVACY_NOTICE_NOT_ACKNOWLEDGED]:
    'Der Hinweis wurde neu geladen. Bitte lies ihn und setze das Häkchen erneut — die vorige Bestätigung galt einem anderen Wortlaut.',
  [ApiErrorCode.IMPERSONATION_READ_ONLY]:
    'Du siehst die Anwendung gerade als jemand anderes. Beende die Ansicht über das Band am oberen Rand, um wieder selbst zu handeln.',
  [ApiErrorCode.DEVICE_ASSIGNMENT_OVERLAP]:
    'Ein Gerät gehört zu einem Zeitpunkt in genau ein Fahrzeug und ein Fahrzeug hat genau ein Gerät. Der Verlauf zeigt, welcher Zeitraum im Weg ist.',
  [ApiErrorCode.USER_ALREADY_EXISTS]:
    'Anlegen ist damit erledigt — die Person steht bereits in der Liste. Möglicherweise hat sie sich gerade selbst zum ersten Mal angemeldet.',
  [ApiErrorCode.SLACK_USER_ALREADY_LINKED]:
    'Ein Slack-Konto gehört genau einer Person. Löse zuerst die bestehende Verknüpfung, wenn sie falsch ist.',
  [ApiErrorCode.SLACK_NOT_CONFIGURED]:
    'Slack ist noch nicht eingerichtet — es fehlen Bot-Token, Signing Secret oder Team-ID in der Konfiguration.',
  [ApiErrorCode.DIRECTORY_PRINCIPAL_NOT_FOUND]:
    'Diese Person gibt es im Verzeichnis nicht mehr. Womöglich wurde das Konto inzwischen gelöscht.',
};

const NEUTRAL_TONES = new Set<string>([
  ApiErrorCode.BOOKING_SLOT_TAKEN,
  ApiErrorCode.MISSING_ROLE,
  ApiErrorCode.DIRECTORY_UNAVAILABLE,
  // „Gibt es schon" ist das gewünschte Ergebnis, nur auf anderem Weg erreicht —
  // in Rot sähe es aus, als wäre etwas schiefgegangen.
  ApiErrorCode.USER_ALREADY_EXISTS,
  ApiErrorCode.SLACK_NOT_CONFIGURED,
  ApiErrorCode.GROUP_RESOLUTION_UNAVAILABLE,
  ApiErrorCode.PRIVACY_NOTICE_NOT_ACKNOWLEDGED,
  ApiErrorCode.IMPERSONATION_READ_ONLY,
]);

export function ErrorNotice({ error }: { error: unknown }) {
  const api = error instanceof ApiError ? error : undefined;
  const message =
    api?.message ?? (error instanceof Error ? error.message : 'Unbekannter Fehler.');
  const advice = api ? ADVICE[api.code] : undefined;
  const soft = api ? NEUTRAL_TONES.has(api.code) : false;

  return (
    <div
      className={[
        'rounded-md border px-3 py-2.5 text-sm',
        soft ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-red-200 bg-red-50 text-red-900',
      ].join(' ')}
      role="alert"
    >
      <p>{message}</p>
      {advice && <p className="mt-1 text-xs opacity-80">{advice}</p>}
      {Array.isArray(api?.details) && (
        <ul className="mt-1.5 list-inside list-disc text-xs">
          {(api.details as { field?: string; message: string }[]).map((detail, index) => (
            <li key={detail.field ?? index}>{detail.message}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
